// kfe-multiple-choice-exercise.js — Multiple Choice testing screen (Brief 058).
//
// PORT TARGET:
//   iOS-Obj-C: Classes/KFEModules/Learning/TextBasedMCQuestions/
//              KFEMultipleChoiceQuesVC.m   (begin page → question table → summary)
//              KFEMultipleChoiceViewModel.m (scoring + the Title.en read)
//              KFEMutipleChoiceSummaryVC.m / …SummaryCells.m (pass/fail report)
//
// Consumes MCEngine (../engines/mc-engine.js) via state snapshots only
// (architecture rule #2). Data comes from the Brief 057 loader: getMcTest(itemId)
// returns the descriptor (title, passScore, sub-type, ordered questions).
//
// Flow (mirrors iOS PageStyle Begin → Testing → Total):
//   • begin   — title + Start button (iOS KFEMultiChoiceBeginPageView).
//   • question — progress "N/total", prompt text, vertical list of choice
//                buttons. Tap → engine.answer(idx) → next question (or summary).
//                iOS has NO correct/wrong reveal — the tap advances immediately.
//   • complete — pass/fail report + shared <kfe-completion-screen> with the
//                iOS-canonical "Keep Going" / "Redo the test" pair (Brief 046/049).
//
// DATA CONTRACT (Brief 057 §7.5 — the single most important constraint):
//   Question + choice content lives in `.en` across the WHOLE 2022 corpus;
//   `.zh` is empty for every question. iOS reads `Title.en` UNCONDITIONALLY
//   (KFEMultipleChoiceViewModel.m:129) regardless of UI language. We mirror
//   that — always read prompt.en / choice.text.en. A naive prompt[currentLang]
//   lookup would blank the entire test in zh-Hans mode.
//
// Sub-types (mcType 1/2/3 — Third Ear / Method / Metaphor) render identically:
//   iOS uses one KFEMultipleChoiceQuesVC for all three. No _renderForSubType.
//
// No audio: MC is text-based ("TextBasedMCQuestions"); the question flow plays
// nothing, so there's no AudioContext-unlock concern here.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { persistCompletion } from "./completion-persist.js";
import { loadMcTests, getMcTest } from "../data/mc-loader.js";
import { MCEngine } from "../engines/mc-engine.js";

export class KFEMultipleChoiceExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: 640px; margin: 0 auto; padding: 0 1rem; box-sizing: border-box; }
    h2 { margin-top: 0; }
    .progress { font-size: 0.85rem; color: var(--kfe-muted);
                text-align: right; margin-bottom: 0.25rem; }
    .prompt { margin: 0.75rem 0; padding: 1rem 1.25rem; border-radius: 12px;
              background: var(--kfe-surface);
              font-size: 1.15rem; font-weight: 600; line-height: 1.5;
              text-align: center; }
    .options { display: grid; gap: 0.5rem; margin-top: 0.75rem; }
    .option { padding: 0.85rem 1rem; border-radius: 10px;
              border: 1px solid var(--kfe-border);
              background: var(--kfe-surface); color: var(--kfe-fg);
              font-size: 1rem; line-height: 1.4; text-align: center;
              cursor: pointer; transition: border-color 0.15s, background 0.15s; }
    /* Brief 186: the orange (var(--kfe-accent) #f89b25) hover border must be
       gated behind a real hovering pointer. On iOS Safari (touch) a bare
       :hover STICKS after a tap, so the just-tapped answer's position kept an
       orange border into the next question's render — surfacing as "each
       question opens with an orange box around one of the answers" on the
       181d MC walk. iOS has NO default highlight at question open: the cell
       border is grey (KFEMultipleChoiceCell.m setAroundViewStyle, 0xb3b3b3),
       selectionStyle is None, and KFEMultipleChoiceQuesVC.m pre-selects no
       row. Only a momentary touch highlight exists (setSelected:), which we
       mirror with :active below (works on touch, never sticks). */
    @media (hover: hover) {
      .option:hover { border-color: var(--kfe-accent); }
    }
    .option:active { background: var(--kfe-surface-hover, #2a2a2a); }

    button.primary { padding: 0.6rem 1.5rem; border-radius: 8px;
                     background: var(--kfe-accent); color: var(--kfe-on-accent);
                     border: 0; cursor: pointer; font-size: 1rem; }

    .start-prompt, .empty { padding: 2rem; text-align: center; }
    .empty { color: var(--kfe-muted); }

    /* Pass/fail report (iOS KFEMutipleChoiceSummaryCells) */
    .report { text-align: center; }
    .report .verdict { font-size: var(--kfe-h2, 1.5rem); font-weight: 600;
                       margin-bottom: 0.25rem; }
    .report .verdict.pass { color: var(--kfe-success, #4caf50); }
    .report .verdict.fail { color: var(--kfe-error, #e57373); }
    .report .report-title { font-size: 0.95rem; font-weight: 600;
                            color: var(--kfe-muted); margin-bottom: 1rem; }
    .report .stat { margin-bottom: 0.75rem; }
    .report .stat .value { font-size: 1.6rem; font-weight: 700; }
    .report .stat .label { font-size: 0.85rem; color: var(--kfe-muted); }
  `;

  /** @type {import("../engines/mc-engine.js").MCEngine | null} */
  engine = null;

  constructor() {
    super();
    this._loaded = false;
    this._noContent = false;
    this._started = false;
    this._completionPersisted = false;
    // Brief 199: a completion write is in flight (so a second terminal event
    // in this lifecycle does not start another one).
    this._completionPending = false;
    /** @type {number | undefined} wall-clock ms this run started (Brief 025) */
    this._startedAtMs = undefined;
    /** @type {import("../data/mc-loader.js").McTest | null} */
    this._test = null;
  }

  async onConnected() {
    await loadMcTests();
    const itemId = /** @type {any} */ (this).itemId;
    const test = getMcTest(itemId);

    // Brief 057 §7.1 content gap: 62 of 224 catalog type-13 items have no
    // descriptor (getMcTest → null). That's a content-ops gap, not a
    // regression — show a friendly empty panel rather than crashing.
    if (!test || !Array.isArray(test.questions) || test.questions.length === 0) {
      this._noContent = true;
      this._loaded = true;
      this._scheduleRender();
      return;
    }

    this._test = test;
    this.engine = new MCEngine(test);
    this._loaded = true;
    this._scheduleRender();
  }

  // ─── User interaction ─────────────────────────────────────────

  _handleStart() {
    if (!this.engine || this._started) return;
    this._started = true;
    // Brief 025: epoch-ms start for the CRM row's time_started / seconds_spent.
    this._startedAtMs = Date.now();
    this.engine.start();
    this._scheduleRender();
  }

  /** @param {number} choiceIndex */
  _handlePick(choiceIndex) {
    if (!this.engine) return;
    this.engine.answer(choiceIndex);
    // Brief 060b: persist completion once the test finishes so this item's
    // words enter the SR Auto met-pool.
    if (this.engine.snapshot().mode === "complete") this._markCompleted();
    this._scheduleRender();
  }

  // Restart the test from question 1 with a fresh engine (iOS redoTheTest:
  // resets playIndex + hadRightCount, then plays the first question again).
  _onRedo() {
    if (!this._test) return;
    this.engine = new MCEngine(this._test);
    this._started = false;
    this._completionPersisted = false;
    // Brief 199: a completion write is in flight (so a second terminal event
    // in this lifecycle does not start another one).
    this._completionPending = false;
    this._handleStart();
  }

  _onReturnToLesson() {
    history.back();
  }

  /** Persist item completion once per view (Brief 060b).
  // Brief 199, criterion 3: the latch is set only AFTER the write has resolved,
  // and a failure leaves it clear so a later terminal event can try again. It
  // used to be set before the write was even attempted, so a rejected write was
  // never retried by this view — six of the 2022 walk's nine lost completions.
  // `_completionPending` keeps a second terminal event in the same lifecycle
  // from starting a second write (learner-store shares the in-flight promise
  // too, so neither can double-count — `completedCount` is a counter, Brief 072).
   */
  async _markCompleted() {
    if (this._completionPersisted || this._completionPending) return;
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) return;
    this._completionPending = true;
    const snap = this.engine?.snapshot();
    const startedAt = this._startedAtMs ?? Date.now();
    // Brief 025: carry the MC score onto the CRM `learner_lesson_items` row.
    const completionWrite = persistCompletion(itemId, { score: Math.round(snap?.score ?? 0), startedAt });
    // Brief 025: the CRM's two dedicated multiple-choice tables. `scores` is
    // INSERT-only (one row per completed run, never revised); `attempts` is an
    // upsert of the running attempt count for the item. Both field lists are an
    // exact match for what the MC engine's snapshot already holds, so neither
    // needs a derived value.
    services.learner.queueMultipleChoiceResult({
      itemId,
      score: Math.round(snap?.score ?? 0),
      pass: snap?.pass === true,
      attempts: snap?.totalQuestions ?? 0,
    });
    // Brief 025: one INSERT-only `learner_test_instances` row per completed run.
    services.learner.recordSession({
      startedAt,
      endedAt: Date.now(),
      exerciseType: "multiple_choice",
      itemId,
      summary: {
        attempts: snap?.totalQuestions ?? 0,
        score: Math.round(snap?.score ?? 0),
        passed: snap?.pass === true,
      },
    }).catch((err) => console.warn("[mc] recordSession failed", err));
    // The CRM rows above are queued regardless — they are this run's record and
    // do not depend on the local completion landing. Only the latch does.
    const ok = await completionWrite;
    this._completionPending = false;
    this._completionPersisted = ok;
  }

  // ─── Render ───────────────────────────────────────────────────

  render() {
    if (!this._loaded) return html`<p>${t("loading")}</p>`;
    if (this._noContent) {
      return html`<div class="empty">
        <h2>${t("mc.title")}</h2>
        <p>${t("mc.empty")}</p>
      </div>`;
    }
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    if (!this._started) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.textContent = t("lesson.start");
      btn.addEventListener("click", () => this._handleStart());
      return html`
        <h2>${t("mc.title")}</h2>
        <div class="start-prompt">${btn}</div>
      `;
    }

    const s = this.engine.snapshot();
    if (s.mode === "complete") return this._renderSummary(s);
    return this._renderQuestion(s);
  }

  /** @param {import("../engines/mc-engine.js").MCState} s */
  _renderQuestion(s) {
    const q = s.currentQuestion;
    if (!q) return html`<p>${t("loading")}</p>`;

    // Brief 057 §7.5: read `.en` unconditionally — never prompt[currentLang].
    const promptText = q.prompt?.en ?? "";

    const optionEls = s.currentChoices.map((choice, idx) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "option";
      btn.textContent = choice.text?.en ?? "";
      btn.addEventListener("click", () => this._handlePick(idx));
      return btn;
    });

    return html`
      <div class="progress">${t("mc.progress", {
        current: String(s.questionIndex + 1),
        total: String(s.totalQuestions),
      })}</div>
      <h2>${t("mc.title")}</h2>
      <div class="prompt">${promptText}</div>
      <div class="options">${optionEls}</div>
    `;
  }

  /** @param {import("../engines/mc-engine.js").MCState} s */
  _renderSummary(s) {
    const percent = Math.round(s.score);
    const verdict = s.pass ? "pass" : "fail";
    const verdictText = s.pass ? t("mc.passed") : t("mc.failed");

    // Brief 046/049: shared completion screen. The pass/fail report goes into
    // the default slot; the iOS-canonical "Keep Going" / "Redo the test" pair
    // comes from the component's `actions`. iOS shows BOTH buttons regardless
    // of outcome (KFEMutipleChoiceSummaryCells), in that order.
    const report = document.createElement("div");
    report.className = "report";

    const v = document.createElement("div");
    v.className = "verdict " + verdict;
    v.textContent = verdictText;

    const reportTitle = document.createElement("div");
    reportTitle.className = "report-title";
    reportTitle.textContent = t("mc.reportTitle");

    const correctStat = this._buildStat(`${percent}%`, t("mc.correctLabel"));
    const requiredStat = this._buildStat(
      `${Math.round(s.passScore)}%`,
      t("mc.requiredLabel"),
    );

    report.append(v, reportTitle, correctStat, requiredStat);

    const screen = document.createElement("kfe-completion-screen");
    /** @type {any} */ (screen).message = t("mc.title");
    /** @type {any} */ (screen).actions = [
      { label: t("completion.multipleChoice.keepGoing"), eventName: "kfe:multipleChoice:keepGoing" },
      { label: t("completion.multipleChoice.redo"), eventName: "kfe:multipleChoice:redo", primary: true },
    ];
    screen.appendChild(report);
    screen.addEventListener("kfe:multipleChoice:keepGoing", () => this._onReturnToLesson());
    screen.addEventListener("kfe:multipleChoice:redo", () => this._onRedo());
    return html`${screen}`;
  }

  /**
   * @param {string} value
   * @param {string} label
   * @returns {HTMLElement}
   */
  _buildStat(value, label) {
    const stat = document.createElement("div");
    stat.className = "stat";
    const v = document.createElement("div");
    v.className = "value";
    v.textContent = value;
    const l = document.createElement("div");
    l.className = "label";
    l.textContent = label;
    stat.append(v, l);
    return stat;
  }
}
